import { type Dayjs } from "dayjs";

import { type DateRange, type DateRangePosition } from "ui/range-picker/range-picker.types";

export const replaceInRange = <T>(
  [start, end]: [T, T],
  position: DateRangePosition,
  item: T,
): [T, T] => (position === "start" ? [item, end] : [start, item]);

/** A half-typed field reports an invalid `Dayjs`, which must not become a bound. */
const validDate = (date: Dayjs | null | undefined) => (date?.isValid() ? date : undefined);

const earlier = (a?: Dayjs, b?: Dayjs) => (a && b ? (a.isBefore(b, "day") ? a : b) : (a ?? b));

const later = (a?: Dayjs, b?: Dayjs) => (a && b ? (a.isAfter(b, "day") ? a : b) : (a ?? b));

type RangeBoundsOptions = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  referenceDate?: Dayjs;
};

type FieldBounds = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  referenceDate?: Dayjs;
};

/**
 * What each field's calendar may offer. The start cannot pass the end and the end cannot precede the
 * start, each on top of the caller's own `minDate` / `maxDate`. The end calendar also opens on the
 * start's month, so picking a range does not mean paging back from today twice.
 */
export const rangeBounds = (
  [start, end]: DateRange,
  { minDate, maxDate, referenceDate }: RangeBoundsOptions,
): { start: FieldBounds; end: FieldBounds } => ({
  start: {
    minDate,
    maxDate: earlier(validDate(end), maxDate),
    referenceDate,
  },
  end: {
    minDate: later(validDate(start), minDate),
    maxDate,
    referenceDate: validDate(start) ?? referenceDate,
  },
});
